import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { IWallet } from '../models/wallet.interface';
import {
  WALLET_BNPL_CLOSE_API,
  WALLET_CACH_IN_API,
  WALLET_CACH_IN_INQUIRY_API,
  WALLET_CACH_OUT_INQUIRY_API,
  WALLET_DETAIL_API as WALLET_DETAILS_API,
} from '../../../data-access/constants/api';
import { TServiceResult } from '../../../data-access/models/base/t-service-resutl';
import { BaseApiService } from '../../../components/core/services/base-api.service';
import { IProcessData, IWalletProcessData } from '../models/wallet-process.interface';
import { IWalletProcess, ProcessActionType } from '../models/wallet-cashin-model.interface';
import { IIpg } from '../models/ipg-model.interface';
import { IReceipt } from '../../../data-access/models/receipt.interface';
import { EWalletPage } from '../models/wallet-page-names.enum';
import {
  CASHOUT_ROUTE,
  NATIONAL_ID_ROUTE,
  RECEIPT_ROUTE,
  WALLET_BNPL_DETAIL_ROUTE,
  WALLET_BNPL_ROUTE,
  WALLET_CASH_IN_ROUTE,
  WALLET_CASH_OUT_ROUTE,
  WALLET_CUTOFF_ROUTE,
  WALLET_IBAN_ROUTE,
  WALLET_TERMS_CONDITIONS_ROUTE,
  WALLETS_ROUTE,
} from '../../../data-access/constants/app-routes';
import { WealthNavigationService } from '@client-monorepo/wealth/navigation';
import { ICancelBnpl } from '../models/cancel-bnpl.interface';

@Injectable({
  providedIn: 'root',
})
export class WalletService {
  private baseService = inject(BaseApiService);
  private navigationService = inject(WealthNavigationService);

  getWalletInfo(walletId: string): Observable<TServiceResult<IWallet>> {
    return this.baseService.get(`${WALLET_DETAILS_API}/${walletId.toLocaleLowerCase()}/details`).pipe(
      catchError((error) => {
        return of(error);
      }),
    );
  }

  walletCashin(processData: IWalletProcessData): Observable<TServiceResult<IIpg>> {
    return this.baseService.post(WALLET_CACH_IN_API, processData).pipe(
      catchError((error) => {
        return of(error);
      }),
    );
  }

  walletCashinInquiry(trackingCode: string): Observable<TServiceResult<IReceipt>> {
    return this.baseService.get(`${WALLET_CACH_IN_INQUIRY_API}?trackingCode=${trackingCode}`).pipe(
      catchError((error) => {
        return of(error);
      }),
    );
  }

  walletCashoutInquiry(trackingCode: string): Observable<TServiceResult<IReceipt>> {
    return this.baseService.get(`${WALLET_CACH_OUT_INQUIRY_API}?trackingCode=${trackingCode}`).pipe(
      catchError((error) => {
        return of(error);
      }),
    );
  }

  walletProcess(apiUrl: string, processData: IWalletProcess): Observable<TServiceResult<IWalletProcessData>> {
    const query = this.buildActionQueryString(processData.action);
    if (this.isString(processData?.amount)) {
      processData.amount = processData?.amount?.replaceAll('٬', '');
    }
    return this.baseService.post(`${apiUrl}${query}`, processData).pipe(
      map((res: TServiceResult<IWalletProcessData>) => {
        if (res.result.action.toLowerCase() === 'page') {
          this._handleWalletPageRedirection(res.result.data.pageName, { ...processData, ...res.result.data }, processData.walletId);
        } else if (res.result.action.toLowerCase() === 'bottomsheet') {
          this._handleWalletPageRedirection(res.result.data.topOnPage, { ...processData, ...res.result.data }, processData.walletId);
        } else if (res.result.action.toLowerCase() === 'exceptionpage') {
          this._handleWalletPageRedirection(res.result.data.topOnPage, { ...processData, ...res.result.data }, processData.walletId);
        }
        return res;
      }),
      catchError((error) => {
        return of(error);
      }),
    );
  }

  walletBnplClose(): Observable<TServiceResult<ICancelBnpl>> {
    return this.baseService.post(WALLET_BNPL_CLOSE_API, null).pipe(
      catchError((err) => {
        return of(err);
      }),
    );
  }

  private isString(value: unknown): value is string {
    return typeof value === 'string';
  }

  private _handleWalletPageRedirection(page: string, state: IProcessData, walletId: string) {
    if (state.url) {
      window.open(state.url, '_self');
    } else {
      switch (page) {
        case EWalletPage.page_wallet_cash_in_landing:
          this.navigationService.navigate([WALLET_CASH_IN_ROUTE, walletId], {
            state,
          });
          break;
        case EWalletPage.page_wallet_cach_out_landing:
          this.navigationService.navigate([WALLET_CASH_OUT_ROUTE, walletId], {
            state,
          });
          break;
        case EWalletPage.page_global_sheba_number:
          this.navigationService.navigate([WALLET_IBAN_ROUTE, walletId], {
            state,
          });
          break;
        case EWalletPage.page_global_customer_birth_date:
          this.navigationService.navigate([WALLET_BNPL_ROUTE, walletId], {
            state,
          });
          break;
        case EWalletPage.page_wallet_bnpl_receipt:
          this.navigationService.navigate([WALLET_BNPL_DETAIL_ROUTE, walletId], {
            state,
          });
          break;
        case EWalletPage.page_wallet_cash_in_terms:
          this.navigationService.navigate([WALLET_TERMS_CONDITIONS_ROUTE, walletId], {
            state,
          });
          break;
        case EWalletPage.page_wallet_cach_out_receipt:
          this.navigationService.navigate([RECEIPT_ROUTE], {
            queryParams: {
              trackingCode: state.cashOutId,
              walletAction: 'cashOut',
            },
          });
          break;
        case EWalletPage.page_wallet_cach_out_process:
          this.navigationService.navigate([CASHOUT_ROUTE, walletId], {
            state,
          });
          break;
        case EWalletPage.page_wallet_landing:
          this.navigationService.navigate([WALLETS_ROUTE, walletId], {
            state,
          });
          break;
        case EWalletPage.page_global_national_id:
          this.navigationService.navigate([NATIONAL_ID_ROUTE], {
            state,
          });
          break;
        case EWalletPage.page_wallet_cash_in_cutoff_time:
          this.navigationService.navigate([WALLET_CUTOFF_ROUTE, walletId], {
            state,
          });
          break;
      }
    }
  }

  private buildActionQueryString(action: ProcessActionType): string {
    return action ? `?action=${action}` : '';
  }
}
